
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    helpWith: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d64gt8abdoed7v0v95v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          helpWith: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#14173A] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37] to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37] mb-6">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Get In Touch</span>
          </div>
          
          <h1 className="text-6xl font-bold text-white mb-6">
            Let's Build Your Safety System
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Schedule a free consultation to discuss your safety goals and challenges.
          </p>
          
          <div className="flex items-center justify-center gap-3 text-gray-400 text-sm">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#D4AF37]">Contact</span>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-[#F7F7F9]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Contact Info Sidebar */}
            <div className="md:col-span-1">
              <div className="bg-[#14173A] rounded-3xl p-8 text-white sticky top-24">
                <h2 className="text-2xl font-bold mb-8">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Service Area</h3>
                      <p className="text-gray-300 text-sm">Minnesota + Wisconsin</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email</h3>
                      <p className="text-gray-300 text-sm">horizonsafetyservices@gmail.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Phone</h3>
                      <p className="text-gray-300 text-sm">(651) 785-3638</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-[#D4AF37] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Response Time</h3>
                      <p className="text-[#D4AF37] text-sm font-medium">Within 24 hours</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-10 pt-8 border-t border-white/20">
                  <h3 className="font-semibold mb-4">Connect With Us</h3>
                  <div className="flex gap-4">
                    <a 
                      href="https://linkedin.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-[#D4AF37] transition-colors cursor-pointer"
                    >
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="md:col-span-2">
              <div className="bg-white rounded-3xl p-12 shadow-xl">
                <h2 className="text-3xl font-bold text-[#14173A] mb-2">Request a Consultation</h2>
                <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
                
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-[#14173A] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all text-[#14173A]"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-[#14173A] mb-2">
                        Company *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all text-[#14173A]"
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-[#14173A] mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all text-[#14173A]"
                          placeholder="your@email.com"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-[#14173A] mb-2">
                          Phone (Optional)
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all text-[#14173A]"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="helpWith" className="block text-sm font-semibold text-[#14173A] mb-2">
                        How can we help? *
                      </label>
                      <select
                        id="helpWith"
                        name="helpWith"
                        required
                        value={formData.helpWith}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all text-[#14173A] cursor-pointer"
                      >
                        <option value="">Select an option...</option>
                        <option value="compliance">Compliance foundation</option>
                        <option value="training">Training</option>
                        <option value="culture">Culture/behavior improvement</option>
                        <option value="indicators">Leading indicators/scorecards</option>
                        <option value="not-sure">Not sure yet</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-[#14173A] mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        maxLength={500}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all text-[#14173A] resize-none"
                        placeholder="Tell us about your safety goals and challenges... (max 500 characters)"
                      ></textarea>
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.message.length}/500
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 bg-[#D4AF37] text-[#14173A] rounded-xl font-bold text-lg hover:bg-[#C9A832] transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <i className="ri-send-plane-fill text-xl"></i>
                        </>
                      )}
                    </button>
                    
                    {submitStatus === 'success' && (
                      <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                            <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                          </div>
                          <div>
                            <h3 className="font-bold text-green-800 text-lg mb-1">Thank You for Reaching Out!</h3>
                            <p className="text-green-700 mb-3">Your message has been received successfully.</p>
                            <div className="space-y-2 text-sm text-green-700">
                              <div className="flex items-center gap-2">
                                <i className="ri-time-line text-green-600"></i>
                                <span>We'll respond within <strong>24 hours</strong></span>
                              </div>
                              <div className="flex items-center gap-2">
                                <i className="ri-mail-check-line text-green-600"></i>
                                <span>A confirmation has been sent to your email</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <i className="ri-phone-line text-green-600"></i>
                                <span>Need urgent help? Call us at <strong>(651) 785-3638</strong></span>
                              </div>
                            </div>
                            <p className="mt-4 text-green-800 font-medium italic">"Safety Built Forward." — We look forward to helping you build a safer workplace.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                        <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-red-800">Something went wrong</p>
                          <p className="text-sm text-red-700">Please try again or contact us directly.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
